import {
    Box,
    Checkbox,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import StyledPagination from '../../common/StyledPagination';
import { Transaction } from '../../../services/transaction.types';
import TransactionTableRow from './TransactionTableRow';
import FilterPanel from '../filters/FilterPanel';
import { TransactionsFilterData } from '../../../services/transaction.types';
import TransactionTableSkeleton from './TransactionTableSkeleton';
import FilledTableSortLabel from '../../../components/common/FilledTableSortLabel';

type Order = 'asc' | 'desc';

interface HeadCell {
    id: keyof Transaction | 'transactionDetails' | 'value' | 'actions' | 'account';
    label: string;
    numeric: boolean;
    sortable: boolean;
}

interface TransactionTableProps {
    transactions: Transaction[];
    selectedIds: number[];
    onRowSelect: (ids: number[]) => void;
    onRowClick: (transaction: Transaction) => void;
    onTransactionDelete: (transaction: Transaction) => void;
    onTransactionEdit: (values: Partial<Transaction>) => void;
    onTransactionSpam: (transaction: Transaction) => void;
    onTransactionNotSpam: (transaction: Transaction) => void;
    onTransactionTaxable: (transaction: Transaction) => void;
    onTransactionNonTaxable: (transaction: Transaction) => void;
    onActionComplete: (message: string) => void;
    order: Order;
    orderBy: string;
    page: number;
    rowsPerPage: number;
    onOrderChange: (order: Order) => void;
    onOrderByChange: (orderBy: string) => void;
    onPageChange: (page: number) => void;
    onRowsPerPageChange: (rowsPerPage: number) => void;
    total: number;
    onFiltersChange?: (filters: TransactionsFilterData) => void;
    initialFilters?: TransactionsFilterData;
    isLoading?: boolean;
}

const TransactionTable: React.FC<TransactionTableProps> = ({
    transactions,
    selectedIds,
    onRowSelect,
    onRowClick,
    onTransactionDelete,
    onTransactionEdit,
    onTransactionSpam,
    onTransactionNonTaxable,
    onTransactionNotSpam,
    onTransactionTaxable,
    onActionComplete,
    order,
    orderBy,
    page,
    rowsPerPage,
    onOrderChange,
    onOrderByChange,
    onPageChange,
    onRowsPerPageChange,
    total,
    onFiltersChange,
    initialFilters,
    isLoading
}) => {
    const { t } = useTranslation();
    const theme = useTheme();

    const headCells: HeadCell[] = [
        { id: 'date', label: t('transactions.table.headers.date'), numeric: false, sortable: true },
        { id: 'account', label: t('transactions.table.headers.account'), numeric: false, sortable: true },
        { id: 'type', label: t('transactions.table.headers.type'), numeric: false, sortable: true },
        { id: 'transactionDetails', label: t('transactions.table.headers.details'), numeric: false, sortable: false },
        { id: 'value', label: t('transactions.table.headers.value'), numeric: true, sortable: true },
        { id: 'actions', label: t('transactions.table.headers.actions'), numeric: false, sortable: false },
    ];

    const handleRequestSort = (property: keyof Transaction) => {
        const isAsc = orderBy === property && order === 'asc';
        onOrderChange(isAsc ? 'desc' : 'asc');
        onOrderByChange(property);
    };

    const handleChangePage = (_: unknown, newPage: number) => {
        onPageChange(newPage);
    };

    const handleChangeRowsPerPage = (event: any) => {
        onRowsPerPageChange(parseInt(event.target.value, 10));
        onPageChange(0);
    };

    const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            const newSelected = transactions.map((n) => n.id as number);
            onRowSelect(newSelected);
            return;
        }
        onRowSelect([]);
    };

    const handleRowClick = (transaction: Transaction) => {
        onRowClick(transaction);
    };

    return (
        <Box
            sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 1,
                overflow: 'hidden',
                bgcolor: 'background.paper',
            }}
        >
            <Box sx={{
                pb: 0,
                bgcolor: 'background.paper'
            }}>
                <FilterPanel
                    onFiltersChange={onFiltersChange || (() => {})}
                    initialFilters={initialFilters}
                    sx={{ width: '100%' }}
                />
            </Box>
            <TableContainer sx={{ overflow: 'auto' }}>
                <Table 
                    stickyHeader 
                    size="medium" 
                    sx={{
                        minWidth: '100%', // Ensures table is at least as wide as its container
                        tableLayout: 'auto', // Allows columns to take their natural width
                        width: 'max-content', // Allows table to expand beyond container width if needed
                        '& .MuiTableCell-root': {
                            py: 2.5,
                            px: 1.5,
                            borderBottom: '2px solid',
                            borderBottomColor: `${theme.palette.divider}`,
                            borderBottomOpacity: 0.6,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        },
                        '& .MuiTableHead-root .MuiTableCell-root': {
                            bgcolor: 'background.paper',
                            fontWeight: 600,
                            borderBottom: '2px solid',
                            borderBottomColor: `${theme.palette.divider}`,
                        },
                        '& .MuiTableBody-root tr td:nth-of-type(5)': { // Details column
                            paddingRight: '4px !important',
                        },
                        '& .MuiTableBody-root tr td:nth-of-type(6)': { // Value column
                            paddingLeft: '4px !important',
                        },
                        '& .MuiTableBody-root tr:last-child td': {
                            borderBottom: '0 !important',
                        },
                        '& .MuiTableBody-root tr:last-child th': {
                            borderBottom: '0 !important',
                        },
                    }}
                >
                    <colgroup>
                        <col style={{ width: 'auto', minWidth: '50px' }} />
                        <col style={{ width: 'auto', minWidth: '120px' }} />
                        <col style={{ width: 'auto', minWidth: '120px' }} />
                        <col style={{ width: 'auto', minWidth: '150px' }} />
                        <col style={{ width: 'auto', minWidth: '280px' }} />
                        <col style={{ width: 'auto', minWidth: '180px' }} />
                        <col style={{ width: 'auto', minWidth: '100px' }} />
                    </colgroup>
                    {/* Column widths: Using fixed minimum widths instead of percentages */}
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox" sx={{ width: '5%' }}>
                                <Checkbox
                                    color="primary"
                                    indeterminate={selectedIds.length > 0 && selectedIds.length < transactions.length}
                                    checked={transactions.length > 0 && selectedIds.length === transactions.length}
                                    onChange={handleSelectAllClick}
                                />
                            </TableCell>
                            {headCells.map((headCell) => (
                                <TableCell
                                    key={headCell.id}
                                    align={headCell.id === 'actions' ? 'right' : headCell.numeric ? 'right' : 'left'}
                                    sortDirection={orderBy === headCell.id ? order : false}
                                >
                                    {headCell.sortable ? (
                                        <FilledTableSortLabel
                                            active={orderBy === headCell.id}
                                            direction={orderBy === headCell.id ? order : 'asc'}
                                            onClick={() => handleRequestSort(headCell.id as keyof Transaction)}
                                        >
                                            {headCell.label}
                                            {orderBy === headCell.id ? (
                                                <Box component="span" sx={visuallyHidden}>
                                                    {order === 'desc' ? t('common.table.sortedDesc') : t('common.table.sortedAsc')}
                                                </Box>
                                            ) : null}
                                        </FilledTableSortLabel>
                                    ) : (
                                        headCell.label
                                    )}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading ? (
                            <TransactionTableSkeleton rowCount={rowsPerPage} />
                        ) : (
                            transactions.map((transaction) => (
                                <TransactionTableRow
                                    key={transaction.id as number}
                                    transaction={transaction}
                                    isSelected={selectedIds.includes(transaction.id as number)}
                                    onSelect={(event) => {
                                        event.stopPropagation();
                                        const selectedIndex = selectedIds.indexOf(transaction.id as number);
                                        let newSelected: number[] = [];

                                        if (selectedIndex === -1) {
                                            newSelected = newSelected.concat(selectedIds, transaction.id as number);
                                        } else {
                                            newSelected = selectedIds.filter((id) => id !== transaction.id);
                                        }

                                        onRowSelect(newSelected);
                                    }}
                                    onClick={() => handleRowClick(transaction)}
                                    onEditSubmit={onTransactionEdit}
                                    onDeleteSubmit={onTransactionDelete}
                                    onSpamSubmit={onTransactionSpam}
                                    onNonTaxableSubmit={onTransactionNonTaxable}
                                    onNotSpamSubmit={onTransactionNotSpam}
                                    onTaxableSubmit={onTransactionTaxable}
                                    onActionComplete={onActionComplete}
                                />
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <StyledPagination
                rowsPerPageOptions={[10, 25, 50]}
                count={total}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage={t('common.table.pagination.rowsPerPage')}
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} ${t('common.table.pagination.of')} ${count}`}
            />
        </Box>
    );
};

export default TransactionTable;
